import { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, Settings2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { formatDate } from '@/lib/utils/date';
import { isExpired } from '@/lib/utils/date';
import { useSubSuperAdmins, useSwitchUser } from '../api';
import type { SubSuperAdminRow } from '../types';

export function SubSuperAdminListPage() {
  const navigate = useNavigate();
  const switchTo = useAuthStore((s) => s.switchTo);
  const switchMutation = useSwitchUser();
  const query = useSubSuperAdmins();
  const rows = query.data ?? [];

  const handleSwitch = useCallback(
    (row: SubSuperAdminRow) => {
      switchMutation.mutate(row.userId, {
        onSuccess: (res) => {
          const incomingRole = Array.isArray(res.role) ? res.role[0] : res.role;
          if (!incomingRole) {
            toast.error('Switch response missing role');
            return;
          }
          switchTo({
            user: { userId: String(res.userid), role: incomingRole },
            token: res.token,
          });
          toast.success(`Switched to ${row.username}`);
          const isSuser = incomingRole === USER_ROLES.SUB_USER;
          navigate(isSuser ? '/sub-user-report' : '/', { replace: true });
        },
        onError: () => toast.error('Could not switch to that user'),
      });
    },
    [navigate, switchMutation, switchTo],
  );

  const columns = useMemo<ColumnDef<SubSuperAdminRow>[]>(
    () => [
      {
        accessorKey: 'userId',
        header: 'ID',
        size: 70,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.userId}</span>
        ),
      },
      {
        accessorKey: 'username',
        header: 'Username',
        cell: ({ row }) => (
          <Link
            to={`/sub-super-admin/update-user/${row.original.userId}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.username}
          </Link>
        ),
      },
      { accessorKey: 'company', header: 'Company' },
      {
        accessorKey: 'parentUser',
        header: 'Parent',
        cell: ({ row }) => row.original.parentUser ?? '—',
      },
      {
        accessorKey: 'expiryDate',
        header: 'Expires',
        size: 130,
        cell: ({ row }) => {
          const v = row.original.expiryDate;
          if (!v) return '—';
          const date = new Date(v.replace(' ', 'T'));
          return Number.isFinite(date.getTime()) ? formatDate(date) : v;
        },
      },
      {
        id: 'status',
        header: 'Status',
        size: 110,
        cell: ({ row }) => {
          if (isExpired(row.original.expiryDate)) {
            return <Badge variant="warning">Expired</Badge>;
          }
          return row.original.deleted === 0 ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="destructive">Inactive</Badge>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions">
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>{row.original.username}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() =>
                  navigate(`/sub-super-admin/update-user/${row.original.userId}`)
                }
              >
                <Pencil className="h-4 w-4" />
                Edit user
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleSwitch(row.original)}
                disabled={switchMutation.isPending}
              >
                <LogIn className="h-4 w-4" />
                Switch to user
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [navigate, switchMutation.isPending, handleSwitch],
  );

  return (
    <FillPage>
      <PageHeader
        title="Admins"
        description="Permission-scoped admins managed under your super-admin account."
        actions={
          <Button asChild>
            <Link to="/sub-super-admin/create-user">
              <Plus className="h-4 w-4" />
              Add sub super admin
            </Link>
          </Button>
        }
      />
      <DataTable<SubSuperAdminRow, unknown>
        tableId="users:sub-super-admin:v6"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search sub super admins…"
        emptyTitle="No sub super admins"
        emptyDescription="Create a sub super admin to delegate scoped admin access."
        getRowId={(row) => String(row.userId)}
        exportFilename="sub-super-admins.csv"
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={10}
      />
    </FillPage>
  );
}
